// campaign-permissions.ts — GM campaign-permission predicate helpers, carved out of template.js
// (roadmap T4.1, 2026-06-16). Byte-faithful port: the only edits versus the template.js originals are
// the `: any` param annotations and nothing else — behavior is
// identical. These are pure/stateless: they read window.FateTide.user, document, and each other; they
// hold no module-local state and have no parse-time caller. Window-attached at the bottom so every
// out-of-bundle consumer (staying template.js callers, the websocket bundle, the render-path readers)
// resolves them as bare-identifier globals at runtime.

/** `tools/encounters/ft3d_scene_host.php` — encounter edit access is enforced by PHP before this shell loads; XZ must not impose extra client gates. */
import { encounterAuthoringActive } from '../shared/encounter-surface';

function ftIsModifyEncounterToolsPage() {
	return typeof document !== 'undefined'
		&& !!(document.getElementById('hidden_input_modify_encounter_id') || document.querySelector('[data-ft-encounter-surface]'))
		&& encounterAuthoringActive();
}

/** Mirrors Auth\UserRole::HIERARCHY rank (0 = lowest). Unknown role → 0. */
type CampaignRecord = Record<string, unknown>;

function recordOf(value: unknown): CampaignRecord | null {
	return value !== null && typeof value === 'object' && !Array.isArray(value)
		? value as CampaignRecord
		: null;
}

function firstOrSelf(value: unknown): unknown {
	return Array.isArray(value) ? value[0] : value;
}

function ftUserPrimaryRoleRank(roleStr: unknown): number {
	const H = ['banned', 'unverified', 'user', 'creator', 'publisher', 'employee', 'admin', 'super_admin'];
	const i = H.indexOf(String(roleStr ?? '').trim());
	return i < 0 ? 0 : i;
}

/**
 * Same 0/1 rule as PHP build_campaign_arr() `campaign_permission` for the *browser* user.
 * Never trust `campaign_arr.campaign_permission` from WebSocket / shared caches — it may be
 * missing (stripped server-side) or from another session. Uses `campaign_gm`,
 * `campaign_created_by_id`, and staff role only.
 *
 * @returns {0|1}
 */
function ftViewerCampaignGmPermission(campaignArr: unknown): 0 | 1 {
	const campaign = recordOf(campaignArr);
	if (!campaign) {
		return 0;
	}
	const uid = String(window.FateTide?.user?.id || '').trim();
	if (!uid) {
		return 0;
	}
	const roleRank = ftUserPrimaryRoleRank(window.FateTide?.user?.primaryRole);
	const employeeRank = ftUserPrimaryRoleRank('employee');
	if (roleRank >= employeeRank) {
		return 1;
	}
	// Campaign reshape (2026-06-04): bucket-first with a legacy [0] fallback.
	const campaigns = recordOf(campaign.campaigns);
	const gmRaw = campaigns?.campaign_gm ?? firstOrSelf(campaign.campaign_gm);
	const crRaw = campaigns?.campaign_created_by_id ?? firstOrSelf(campaign.campaign_created_by_id);
	const gm = gmRaw != null ? String(gmRaw).trim() : '';
	const cr = crRaw != null ? String(crRaw).trim() : '';
	if (gm !== '' && gm === uid) {
		return 1;
	}
	if (cr !== '' && cr === uid) {
		return 1;
	}
	return 0;
}

/**
 * Mutates `campaign_arr` so `campaign_permission[0]` matches this viewer (PHP build_campaign_arr).
 * Call whenever a campaign snapshot is fetched or merged into `campaignCache` / sidelog.
 */
function ftApplyCampaignPermissionForViewer(campaignArr: unknown): unknown {
	const campaign = recordOf(campaignArr);
	if (!campaign) {
		return campaignArr;
	}
	// Phase 5A.1 round 5 (2026-05-29): write ONLY to permissions bucket (boolean).
	// Legacy campaign_permission top-level (wrapped int) RETIRED.
	const p = ftViewerCampaignGmPermission(campaignArr);
	const permissions = recordOf(campaign.permissions) ?? {};
	campaign.permissions = { ...permissions, campaign: p === 1 };
	return campaignArr;
}

function ftUserIsCampaignGmFromCampaignArr(campaignArr: unknown): boolean {
	return ftViewerCampaignGmPermission(campaignArr) === 1;
}

// ===========================================================================================
// Window attachment — the public surface the original top-level functions exposed. ftIsModifyEncounterToolsPage
// is read by the render path (creature-render / encounter-map drag+XZ / inventory / websocket — each declares
// it in its own globals.d.ts); ftApplyCampaignPermissionForViewer + ftUserIsCampaignGmFromCampaignArr are called
// by template.js's staying getCampaignArr / getEncounterSettingSummary and by the websocket campaign-update bundle
// (already declared in websocket/globals.d.ts). ftUserPrimaryRoleRank + ftViewerCampaignGmPermission have no known
// out-of-bundle caller but are window-attached as the faithful default (they were top-level globals; over-exposing
// is harmless, a missed bare ref would silently break GM gating).
// ===========================================================================================
declare global {
	interface Window {
		ftIsModifyEncounterToolsPage: typeof ftIsModifyEncounterToolsPage;
		ftUserPrimaryRoleRank: typeof ftUserPrimaryRoleRank;
		ftViewerCampaignGmPermission: typeof ftViewerCampaignGmPermission;
		ftApplyCampaignPermissionForViewer: typeof ftApplyCampaignPermissionForViewer;
		ftUserIsCampaignGmFromCampaignArr: typeof ftUserIsCampaignGmFromCampaignArr;
	}
}

Object.assign(window, {
	ftIsModifyEncounterToolsPage,
	ftUserPrimaryRoleRank,
	ftViewerCampaignGmPermission,
	ftApplyCampaignPermissionForViewer,
	ftUserIsCampaignGmFromCampaignArr,
});

export {};
